'use client';

import { useState, useRef, useEffect } from 'react';

export default function InterclupsPage() {
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [hasMatches, setHasMatches] = useState<boolean | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    // Vérifier s'il y a des matchs pour la date sélectionnée
    const checkMatches = async () => {
      setHasMatches(null); // Loading state
      try {
        const apiUrl = `/api/check-matches?date=${selectedDate}`;
        const response = await fetch(apiUrl);
        const data = await response.json();
        setHasMatches(data.hasMatches);
      } catch (e) {
        // En cas d'erreur, afficher l'iframe par défaut
        console.error('Erreur lors de la vérification des matchs:', e);
        setHasMatches(true);
      }
    };

    checkMatches();
  }, [selectedDate]);

  // Convertir la date en format pour mylivescore (YYYY-MM-DD)
  const iframeUrl =
    selectedDate && selectedDate !== 'all'
      ? `https://mylivescore.fr/m?club=sch&dtj=${selectedDate}`
      : 'https://mylivescore.fr/m?club=sch';

  const handlePreviousDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() - 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    setSelectedDate(`${year}-${month}-${day}`);
  };

  const handleNextDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    setSelectedDate(`${year}-${month}-${day}`);
  };

  const handleToday = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    setSelectedDate(`${year}-${month}-${day}`);
  };

  const formattedDate = new Date(selectedDate + 'T00:00:00').toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero */}
      <section className="bg-gradient-to-r from-[#006C8F] to-[#005570] text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-block px-6 py-2 bg-[#FFC72C] text-[#006C8F] font-bold rounded-full mb-4 text-sm tracking-widest uppercase">
            Suivi en direct
          </div>
          <h1
            className="text-5xl md:text-6xl font-bold mb-4"
            style={{ fontFamily: 'var(--font-playfair), serif' }}
          >
            Interclubs
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            Suivez en direct tous les matchs interclubs du Sporting Club Houlgate
          </p>
        </div>
      </section>

      {/* Sélecteur de date */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 flex-wrap">
            <button
              onClick={handlePreviousDay}
              className="px-4 py-2 bg-[#006C8F] text-white rounded-lg hover:bg-[#005570] transition-colors font-semibold text-sm"
            >
              ← Jour précédent
            </button>

            <div className="flex items-center gap-2 sm:gap-4">
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C8F] text-sm sm:text-base"
              />
              <span className="text-gray-600 text-sm sm:text-base min-w-fit capitalize">
                {formattedDate}
              </span>
            </div>

            <button
              onClick={handleNextDay}
              className="px-4 py-2 bg-[#006C8F] text-white rounded-lg hover:bg-[#005570] transition-colors font-semibold text-sm"
            >
              Jour suivant →
            </button>

            <button
              onClick={handleToday}
              className="px-4 py-2 bg-[#FFC72C] text-[#006C8F] rounded-lg hover:bg-[#ffb700] transition-colors font-semibold text-sm"
            >
              Aujourd’hui
            </button>
          </div>
        </div>
      </section>

      {/* iframe MyLiveScore */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {hasMatches === null && (
              <div className="h-[300px] flex items-center justify-center text-gray-400">
                Chargement...
              </div>
            )}
            {hasMatches === false && (
              <div className="h-[300px] flex items-center justify-center">
                <div className="text-center">
                  <p className="text-gray-500 text-lg">Pas de match prévu pour cette date</p>
                  <p className="text-gray-400 text-sm mt-2">Sélectionnez une autre date</p>
                </div>
              </div>
            )}
            {hasMatches === true && (
              <iframe
                ref={iframeRef}
                src={iframeUrl}
                width="100%"
                height="900"
                frameBorder="0"
                scrolling="auto"
                style={{ border: 'none' }}
                title="Scores MyLiveScore"
              />
            )}
          </div>
          <p className="text-center text-gray-500 text-sm mt-4">
            Les données sont mises à jour automatiquement. Rechargez la page pour les données les
            plus récentes.
          </p>
        </div>
      </section>
    </div>
  );
}
